import React, {FC, memo} from "react";
import {Formik, FormikProps} from "formik";
import {DbTemplate} from "./Email";
import {
    EditTemplateInput,
    GetTemplateByIdQuery,
    MailDataType,
    useUpdateTemplateMutation,
} from "../../../generated/graphql";
import LayoutForm from "../../../views/LayoutForm/LayoutForm";
import css from "./Email.less";
import TemplateInputField from "../shared/TemplateInputField";

type EmailFormTemplateInput = EditTemplateInput & {id: string};

type EmailEditorProps = {
    dbTemplate: DbTemplate;
    mailDataType: MailDataType;
    setFormikProps: (formikProps: FormikProps<EditTemplateInput>) => void;
    setEditMode: (editMode: boolean) => void;
    isNoTemplate?: boolean;
    /** Ungespeicherter Entwurf aus dem Parent; fehlt er, wird aus der gespeicherten Vorlage initialisiert. */
    draft?: EditTemplateInput;
    setDraft: (draft: EditTemplateInput | undefined) => void;
};

/**
 * Meldet die aktuelle Formik-Instanz einmalig nach dem Mount an den Parent.
 * Der Wechsel Vorschau <-> Editor unmountet Formik; ohne diese Meldung behielte der
 * Parent den Snapshot der alten, bereits unmounteten Instanz und "Aenderungen speichern"
 * wuerde auf einer toten Instanz arbeiten (#820).
 */
const FormikPropsReporter: FC<{formikProps: FormikProps<EditTemplateInput>; setFormikProps: (formikProps: FormikProps<EditTemplateInput>) => void}> = ({formikProps, setFormikProps}) => {
    const latest = React.useRef(formikProps);
    latest.current = formikProps;

    React.useEffect(() => {
        setFormikProps(latest.current);
        // Nur beim Mount: spaetere Aktualisierungen laufen ueber handleTextChange.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return null;
};

const EmailEditor: FC<EmailEditorProps> = ({
    dbTemplate,
    mailDataType,
    setFormikProps,
    setEditMode,
    isNoTemplate,
    draft,
    setDraft,
}) => {
    const [
        updateMutation,
        {loading: updateLoading, error: createUpdateError},
    ] = useUpdateTemplateMutation();

    return (
        <Formik<EditTemplateInput>
            initialValues={(draft ?? dbTemplate.data?.template) as EditTemplateInput}
            validationSchema={{}}
            onSubmit={(values, formikActions) => {
                if (dbTemplate.data?.template?.id) {
                    const id = dbTemplate.data.template.id;

                    const editTemplateData: EditTemplateInput = {
                        subject: values.subject,
                        headerText: values.headerText,
                        footerText: values.footerText,
                        signature: values.signature,
                    };

                    try {
                        updateMutation({
                            variables: {
                                id,
                                editTemplateData,
                            },
                        }).then(() => {
                            // Nach dem Speichern gibt es keinen ungespeicherten Entwurf mehr:
                            // verwerfen und die gespeicherte Vorlage nachladen (#820).
                            setDraft(undefined);
                            dbTemplate.refetch();
                            setEditMode(false);
                        });
                    } catch (error) {
                        formikActions.setStatus(error);
                    } finally {
                        formikActions.setSubmitting(false);
                    }
                }
            }}
        >
            {(formikProps) => {
                const {values, setFieldValue} = formikProps;

                const handleTextChange = (
                    description: string,
                    content: string,
                ) => {
                    setFieldValue(description, content);
                    const updatedValues = {
                        ...formikProps.values,
                        [description]: content,
                    };

                    setFormikProps({...formikProps, values: updatedValues});
                    setDraft(updatedValues);
                };

                const typedValues = values as GetTemplateByIdQuery["template"];

                return (
                    <div
                        // style={{
                        //     border: "2px solid red",
                        //     overflow: "auto",
                        // }}
                        className={css.emailFormContainer}
                    >
                        <FormikPropsReporter formikProps={formikProps} setFormikProps={setFormikProps} />
                        {dbTemplate.data?.template && (
                            <LayoutForm
                                name="emailForm"
                                columns={1}
                                className={css.emailForm}
                                // layout="horizontal"
                                layout={
                                    isNoTemplate ? "vertical" : "horizontal"
                                }
                            >
                                <TemplateInputField
                                    field="subject"
                                    templateContent={typedValues}
                                    handleTextChange={handleTextChange}
                                    mailDataType={mailDataType}
                                    isNoTemplate={isNoTemplate}
                                    renderAs="formItem"
                                    className={css.textArea}
                                    variablesMenuClassName={css.variablesMenu}
                                />
                                <TemplateInputField
                                    field="headerText"
                                    templateContent={typedValues}
                                    handleTextChange={handleTextChange}
                                    mailDataType={mailDataType}
                                    isNoTemplate={isNoTemplate}
                                    minRows={isNoTemplate ? 20 : 10}
                                    renderAs="formItem"
                                    className={css.textArea}
                                    variablesMenuClassName={css.variablesMenu}
                                />
                                {!isNoTemplate && (
                                    <>
                                        <TemplateInputField
                                            field="footerText"
                                            templateContent={typedValues}
                                            handleTextChange={handleTextChange}
                                            mailDataType={mailDataType}
                                            renderAs="formItem"
                                            className={css.textArea}
                                            variablesMenuClassName={css.variablesMenu}
                                        />
                                        <TemplateInputField
                                            field="signature"
                                            templateContent={typedValues}
                                            handleTextChange={handleTextChange}
                                            mailDataType={mailDataType}
                                            renderAs="formItem"
                                            className={css.textArea}
                                            variablesMenuClassName={css.variablesMenu}
                                        />
                                    </>
                                )}
                            </LayoutForm>
                        )}
                    </div>
                );
            }}
        </Formik>
    );
};

export default memo(EmailEditor);
